<template>
  <section class="graph-section">
    <div class="graph-section__header reveal-child" :style="delay(0.1)">
      <h2 class="graph-section__title">关系图谱</h2>
      <p class="graph-section__subtitle">GUAN XI TU PU</p>
    </div>

    <div class="graph-section__body reveal-child" :style="delay(0.3)">
      <aside class="graph-panel">
        <div class="graph-panel__tabs">
          <button
            v-for="tab in queryTabs"
            :key="tab.value"
            type="button"
            class="graph-panel__tab"
            :class="{ 'graph-panel__tab--active': activeTab === tab.value }"
            @click="handleTabChange(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>

        <template v-if="activeTab === 'node'">
          <div class="graph-panel__field">
            <label class="graph-panel__label">选择类型：</label>
            <div class="graph-panel__types">
              <button
                v-for="type in nodeTypes"
                :key="type.value"
                type="button"
                class="graph-panel__type"
                :class="{ 'graph-panel__type--active': selectedType === type.value }"
                @click="toggleType(type.value)"
              >
                {{ type.label }}
              </button>
            </div>
          </div>

          <div class="graph-panel__field">
            <el-input v-model="nodeName" placeholder="输入节点名称" />
          </div>

          <div class="graph-panel__field graph-panel__field--row">
            <label class="graph-panel__label">数量：</label>
            <el-select v-model="nodeCount" class="graph-panel__select">
              <el-option label="30" :value="30" />
              <el-option label="60" :value="60" />
              <el-option label="100" :value="100" />
            </el-select>
          </div>
        </template>

        <template v-else-if="activeTab === 'relation'">
          <div class="graph-panel__field">
            <label class="graph-panel__label">起始节点：</label>
            <el-input
              v-model="startNodeName"
              placeholder="输入关键词"
              clearable
            />
          </div>

          <div class="graph-panel__field">
            <label class="graph-panel__label">尾节点：</label>
            <el-input
              v-model="endNodeName"
              placeholder="输入关键"
              clearable
            />
          </div>
          <p class="graph-panel__hint">起止节点只需填写一侧；按名称模糊匹配相关节点与关系。</p>
        </template>

        <div class="graph-panel__actions">
          <button type="button" class="graph-panel__btn graph-panel__btn--reset" @click="handleReset">
            重置
          </button>
          <button
            type="button"
            class="graph-panel__btn graph-panel__btn--search"
            :disabled="searching"
            @click="handleSearch"
          >
            {{ searching ? '查询中...' : '查询' }}
          </button>
        </div>
      </aside>

      <div class="graph-section__canvas-wrap">
        <div v-if="searching" class="graph-section__overlay">加载中...</div>
        <div v-else-if="searched && !graphNodes.length" class="graph-section__overlay">暂无图谱数据</div>
        <div ref="chartRef" class="graph-section__canvas" />
        <GraphCategoryLegend
          v-if="graphNodes.length"
          :nodes="graphNodes"
          :value="highlightedCategory"
          @input="handleCategoryFilter"
        />

        <aside v-if="panelVisible" class="graph-attr-panel">
          <header class="graph-attr-panel__header">
            <div class="graph-attr-panel__heading">
              <h3 class="graph-attr-panel__title">{{ selectedNode ? selectedNode.name : '实体属性' }}</h3>
              <p v-if="selectedNode && selectedNode.identityName" class="graph-attr-panel__role">
                {{ selectedNode.identityName }}
              </p>
            </div>
            <button type="button" class="graph-attr-panel__close" aria-label="关闭" @click="closeAttrPanel">
              <i class="el-icon-close" />
            </button>
          </header>
          <GraphAttributeList
            :fields="selectedAttributes"
            :entity-id="selectedNode && selectedNode.entityId"
            :loading="attributeLoading"
            :error="panelError"
          />
        </aside>
      </div>
    </div>
  </section>
</template>

<script>
import {
  fetchGraphIdentityOptions,
  queryHomeGraph,
  fetchKnowledgeEntityAttribute,
  mapKnowledgeGraph,
  mapEntityAttribute
} from '@/api/knowledge'
import {
  createGraphVis,
  destroyGraphVis,
  buildGraphVisPayload,
  renderGraphVis,
  pickBusinessNode,
  bindNeighborHighlight,
  bindLinkInteractions,
  highlightGraphCategory,
  focusGraphNeighbors,
  clearGraphNeighborFocus,
  applyGraphNodeImages,
  fitGraphVis
} from '@/utils/graphVisHelper'
import GraphCategoryLegend from '@/components/graph/GraphCategoryLegend.vue'
import GraphAttributeList from '@/components/graph/GraphAttributeList.vue'

const PERSON_TYPE_LABELS = ['人物', '荣氏人物']

export default {
  name: 'RelationshipGraphSection',
  components: { GraphCategoryLegend, GraphAttributeList },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visgraph: null,
      eventsBound: false,
      activeTab: 'node',
      selectedType: null,
      nodeName: '',
      nodeCount: 60,
      startNodeName: '',
      endNodeName: '',
      nodeTypes: [],
      graphNodes: [],
      searching: false,
      searched: false,
      selectedNode: null,
      selectedAttributes: [],
      attributeLoading: false,
      panelError: '',
      attributeRequestId: 0,
      graphRenderId: 0,
      highlightedCategory: '',
      personIdentityIds: [],
      queryTabs: [
        { label: '节点查询', value: 'node' },
        { label: '关系查询', value: 'relation' }
      ]
    }
  },
  computed: {
    panelVisible: {
      get() {
        return !!this.selectedNode
      },
      set(value) {
        if (!value) {
          this.selectedNode = null
          this.selectedAttributes = []
          this.panelError = ''
        }
      }
    }
  },
  watch: {
    visible(val) {
      if (val && this.visgraph) {
        this.$nextTick(() => fitGraphVis(this.visgraph))
      }
    }
  },
  mounted() {
    this.loadIdentityOptions()
    this.$nextTick(() => {
      this.loadGraph(true)
    })
  },
  beforeDestroy() {
    destroyGraphVis(this.visgraph)
    this.visgraph = null
    this.eventsBound = false
  },
  methods: {
    delay(seconds) {
      return {
        transitionDelay: this.visible ? `${seconds}s` : '0s'
      }
    },
    async loadIdentityOptions() {
      try {
        const list = await fetchGraphIdentityOptions()
        this.nodeTypes = this.mergePersonTypes((list || []).map((item) => ({
          label: item.label,
          value: item.id
        })))
      } catch (error) {
        this.$message.error(error.message || '加载类型失败')
      }
    },
    mergePersonTypes(list) {
      const persons = list.filter((item) => PERSON_TYPE_LABELS.includes(item.label))
      const others = list.filter((item) => !PERSON_TYPE_LABELS.includes(item.label))
      this.personIdentityIds = persons.map((item) => item.value).filter((id) => id != null)
      if (!persons.length) {
        return list
      }
      const primary = persons.find((item) => item.label === '人物') || persons[0]
      return [{ ...primary, label: '人物' }, ...others]
    },
    toggleType(value) {
      this.selectedType = this.selectedType === value ? null : value
    },
    handleTabChange(tab) {
      this.activeTab = tab
      this.clearEndpointFields()
    },
    clearEndpointFields() {
      this.startNodeName = ''
      this.endNodeName = ''
    },
    buildGraphPayload() {
      if (this.activeTab === 'node') {
        const payload = {
          queryType: 'node',
          nodeName: this.nodeName.trim(),
          isAccurate: 0,
          limit: this.nodeCount
        }
        if (this.selectedType != null) {
          payload.identityId = this.selectedType
          if (this.personIdentityIds.includes(this.selectedType)) {
            payload.identityIds = this.personIdentityIds
          }
        }
        return payload
      }
      return {
        queryType: 'relation',
        startKeyword: this.startNodeName.trim() || undefined,
        endKeyword: this.endNodeName.trim() || undefined,
        limit: 300
      }
    },
    async loadGraph(useDefault = false) {
      this.searching = true
      this.searched = true
      try {
        const data = useDefault
          ? await queryHomeGraph({
            queryType: 'node',
            nodeName: '',
            isAccurate: 0,
            limit: this.nodeCount
          })
          : await queryHomeGraph(this.buildGraphPayload())
        await this.applyGraphData(data || {})
      } catch (error) {
        this.graphNodes = []
        if (this.visgraph) {
          this.visgraph.clearAll()
        }
        this.$message.error(error.message || '查询失败')
      } finally {
        this.searching = false
      }
    },
    async applyGraphData(data) {
      const mapped = mapKnowledgeGraph(data)
      const renderId = ++this.graphRenderId
      this.graphNodes = mapped.nodes.map((node) => ({
        id: node.id,
        name: node.name,
        entityId: node.entityId,
        identityName: node.identityName,
        category: node.category
      }))
      this.highlightedCategory = ''
      if (!mapped.nodes.length) {
        if (this.visgraph) {
          this.visgraph.clearAll()
        }
        return
      }
      const payload = await buildGraphVisPayload(mapped, { skipImages: true })
      if (renderId !== this.graphRenderId) {
        return
      }
      await this.$nextTick()
      const graph = this.ensureGraph()
      if (!graph || renderId !== this.graphRenderId) {
        return
      }
      renderGraphVis(graph, payload, { charge: -280 })
      this.$nextTick(() => fitGraphVis(graph))
      applyGraphNodeImages(graph, mapped).catch(() => {})
    },
    handleCategoryFilter(category) {
      this.highlightedCategory = category || ''
      highlightGraphCategory(this.visgraph, this.highlightedCategory)
      fitGraphVis(this.visgraph)
    },
    ensureGraph() {
      if (this.visgraph) {
        return this.visgraph
      }
      const container = this.$refs.chartRef
      if (!container) {
        return null
      }
      try {
        this.visgraph = createGraphVis(container, { dark: false })
        this.bindGraphEvents()
        return this.visgraph
      } catch (error) {
        console.error(error)
        this.$message.error(error.message || '图谱初始化失败')
        return null
      }
    },
    bindGraphEvents() {
      if (!this.visgraph || this.eventsBound) {
        return
      }
      this.eventsBound = true
      const that = this
      this.visgraph.registEventListener('node', 'click', (event, node) => {
        const business = pickBusinessNode(node)
        that.highlightedCategory = ''
        if (business && that.visgraph && String(that.visgraph._highlightNodeId) === String(business.id)) {
          that.closeAttrPanel()
          return
        }
        that.focusClickedNode(business)
        that.handleNodeClick(business)
      })
      this.visgraph.registEventListener('scene', 'whiteSpaceClick', () => {
        that.closeAttrPanel()
      })
      bindNeighborHighlight(this.visgraph)
      bindLinkInteractions(this.visgraph, {
        onClick() {
          that.highlightedCategory = ''
          that.selectedNode = null
          that.selectedAttributes = []
          that.panelError = ''
        }
      })
    },
    focusClickedNode(node) {
      if (!this.visgraph || !node || !node.id) return
      focusGraphNeighbors(this.visgraph, node.id)
    },
    async handleNodeClick(node) {
      this.selectedNode = node
      this.selectedAttributes = []
      this.panelError = ''
      if (!node || !node.entityId) {
        this.panelError = node ? '该节点暂无实体属性' : ''
        return
      }
      await this.loadNodeAttributes(node.entityId)
    },
    async loadNodeAttributes(entityId) {
      const requestId = ++this.attributeRequestId
      this.attributeLoading = true
      this.panelError = ''
      this.selectedAttributes = []
      try {
        const list = await fetchKnowledgeEntityAttribute(entityId)
        if (requestId !== this.attributeRequestId) {
          return
        }
        this.selectedAttributes = (list || []).map(mapEntityAttribute)
      } catch (error) {
        if (requestId !== this.attributeRequestId) {
          return
        }
        this.selectedAttributes = []
        this.panelError = '属性加载失败'
      } finally {
        if (requestId === this.attributeRequestId) {
          this.attributeLoading = false
        }
      }
    },
    closeAttrPanel() {
      clearGraphNeighborFocus(this.visgraph)
      this.selectedNode = null
      this.selectedAttributes = []
      this.panelError = ''
    },
    handleReset() {
      this.nodeName = ''
      this.nodeCount = 60
      this.selectedType = null
      this.clearEndpointFields()
      this.closeAttrPanel()
      this.loadGraph(true)
    },
    handleSearch() {
      if (this.activeTab === 'relation') {
        const start = this.startNodeName.trim()
        const end = this.endNodeName.trim()
        if (!start && !end) {
          this.$message.warning('请至少填写起始节点或尾节点关键词')
          return
        }
      }
      this.loadGraph(false)
    }
  }
}
</script>

<style lang="scss" scoped>
.graph-section {
  padding: $spacing-4xl 0;
  background:
    url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"),
    $color-paper;

  &__header {
    @include page-container;
    margin-bottom: $spacing-2xl;
  }

  &__title {
    font-family: $font-serif;
    font-size: clamp(#{rem(22)}, 2.5vw, #{rem(32)});
    font-weight: 700;
    color: $color-brown-dark;
    letter-spacing: 2px;
  }

  &__subtitle {
    margin-top: $spacing-xs;
    font-size: rem(11);
    letter-spacing: 3px;
    color: $color-text-light;
  }

  &__body {
    display: flex;
    gap: $spacing-xl;
    @include page-container;
    min-height: rem(520);
  }

  &__canvas-wrap {
    position: relative;
    flex: 1;
    min-width: 0;
    background: rgba($color-cream, 0.4);
    border-radius: 4px;
    overflow: hidden;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba($color-cream, 0.72);
    color: $color-text;
    font-size: rem(14);
  }

  &__canvas {
    width: 100%;
    height: rem(520);
  }
}

.graph-attr-panel {
  position: absolute;
  top: 50%;
  right: rem(16);
  z-index: 4;
  width: rem(320);
  max-width: calc(100% - #{rem(32)});
  max-height: calc(100% - #{rem(48)});
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  background: rgba($color-cream, 0.96);
  border: 1px solid rgba($color-brown-dark, 0.12);
  border-radius: 6px;
  box-shadow: 0 8px 28px rgba($color-brown-dark, 0.16);
  padding: $spacing-base $spacing-md;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: $spacing-sm;
    margin-bottom: $spacing-sm;
  }

  &__heading {
    min-width: 0;
  }

  &__title {
    font-family: $font-serif;
    font-size: rem(16);
    font-weight: 700;
    color: $color-brown-dark;
    line-height: 1.4;
  }

  &__role {
    margin-top: rem(4);
    font-size: rem(12);
    color: $success-color;
  }

  &__close {
    flex-shrink: 0;
    width: rem(28);
    height: rem(28);
    border-radius: 4px;
    color: $color-text-light;
    font-size: rem(16);

    &:hover {
      background: rgba($color-brown-dark, 0.08);
      color: $color-brown-dark;
    }
  }

  &__status {
    font-size: rem(13);
    color: $color-text-light;
    line-height: 1.7;
  }

  &__meta {
    display: flex;
    flex-direction: column;
    gap: rem(10);
    overflow-y: auto;
  }

  &__row {
    display: flex;
    font-size: rem(13);
    line-height: 1.7;

    dt {
      flex-shrink: 0;
      color: $color-text-light;
    }

    dd {
      color: $color-brown-dark;
      word-break: break-all;
    }
  }

  &__media dt {
    display: block;
    font-size: rem(12);
    color: $color-text-light;
    margin-bottom: rem(6);
  }

  &__images {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: rem(8);

    img {
      width: 100%;
      height: rem(72);
      object-fit: cover;
      border-radius: 4px;
    }
  }
}

.graph-panel {
  width: rem(280);
  max-width: 100%;
  flex-shrink: 0;
  background: rgba($color-cream, 0.95);
  padding: $spacing-base;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba($color-brown-dark, 0.08);
  display: flex;
  flex-direction: column;
  gap: $spacing-base;

  &__tabs {
    display: flex;
    gap: $spacing-xs;
  }

  &__tab {
    flex: 1;
    padding: $spacing-sm;
    font-size: rem(12);
    border-radius: 4px;
    background: $color-paper-light;
    color: $color-text;
    transition: all 0.2s;

    &--active {
      background: $color-brown-dark;
      color: $color-white;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: $spacing-sm;

    &--row {
      flex-direction: row;
      align-items: center;
    }
  }

  &__label {
    flex-shrink: 0;
    font-size: rem(13);
    color: $color-text;
    white-space: nowrap;
  }

  &__hint {
    margin: -$spacing-xs 0 $spacing-sm;
    font-size: rem(12);
    line-height: 1.5;
    color: $color-text-light;
  }

  &__types {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-xs;
  }

  &__type {
    padding: $spacing-xs $spacing-md;
    font-size: rem(12);
    border-radius: 4px;
    border: 1px solid $color-border;
    background: $color-white;
    transition: all 0.2s;

    &--active {
      background: $color-brown-dark;
      color: $color-white;
      border-color: $color-brown-dark;
    }
  }

  &__select,
  &__autocomplete,
  &__number {
    width: 100%;
    flex: 1;
  }

  &__actions {
    display: flex;
    gap: $spacing-sm;
    margin-top: auto;
  }

  &__btn {
    flex: 1;
    padding: $spacing-md;
    font-size: rem(14);
    border-radius: 4px;
    color: $color-white;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    &--reset {
      background: $color-brown-dark;
    }

    &--search {
      background: #1e3a5f;
    }
  }
}

@media (max-width: 960px) {
  .graph-section {
    &__body {
      flex-direction: column;
    }

    &__canvas {
      height: rem(400);
    }
  }

  .graph-panel {
    width: 100%;
  }

  .graph-attr-panel {
    width: rem(280);
  }
}
</style>
